import React, { useCallback, useMemo } from 'react';

import { Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';

interface Props {
  readonly shapes: readonly (readonly Point2DInterface[])[];

  readonly color: string;

  readonly maxWidth: number;
  readonly maxHeight: number;

  readonly sx?: React.CSSProperties;
  readonly onClick?: () => void;
}

const MARGIN = 1;

export function CanvasPolygons(props: Props) {
  const { shapes, maxWidth, maxHeight, color, sx: style, onClick } = props;

  const { xRange, yRange, translatedShapes } = useMemo(() => {
    const allVertices = shapes.flat();
    const allXs = allVertices.map(({ x }) => x);
    const allYs = allVertices.map(({ y }) => y);
    const minX = Math.min(...allXs);
    const maxX = Math.max(...allXs);
    const xRange = maxX - minX;
    const minY = Math.min(...allYs);
    const maxY = Math.max(...allYs);
    const yRange = maxY - minY;

    const xTranslation = minX * -1;
    const yTranslation = minY * -1;

    const translatedShapes = shapes.map((shape) =>
      shape.map((vertex) => ({ x: vertex.x + xTranslation, y: vertex.y + yTranslation })),
    );

    return { xRange, yRange, translatedShapes };
  }, [shapes]);

  const previewWidth = maxWidth - MARGIN;
  const previewHeight = maxHeight - MARGIN;
  const widthScale = previewWidth / xRange;
  const heightScale = previewHeight / yRange;

  const scale = Math.min(widthScale, heightScale);

  const drawingWidth = xRange * scale + MARGIN;
  const drawingHeight = yRange * scale + MARGIN;

  const canvasRef = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('Could not find 2D context to render CanvasPolygons');
        return;
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.translate(MARGIN / 2, drawingHeight - MARGIN / 2);
      ctx.scale(scale, -scale);

      ctx.fillStyle = color;

      for (const shape of translatedShapes) {
        ctx.beginPath();
        ctx.moveTo(shape[0].x, shape[0].y);

        for (let i = 1; i < shape.length; i++) ctx.lineTo(shape[i].x, shape[i].y);

        ctx.fill();
      }
    },
    [drawingHeight, color, scale, translatedShapes],
  );

  if (xRange <= 0 || yRange <= 0 || !Number.isFinite(xRange) || !Number.isFinite(yRange)) return null;

  return <canvas style={style} onClick={onClick} ref={canvasRef} width={drawingWidth} height={drawingHeight} />;
}
